import { useEffect, useState } from "react";
import "./obondo-bubble.css";

/**
 * ObondoBubble — floating chat bubble showing Obondo (the trilingual AI copilot)
 * speaking to the user, positioned next to phone/laptop mockups.
 *
 * Replaces the decorative QR chip. Instead of "SCAN to try", it shows a
 * live-typed one-liner in Sheng/Kiswahili/English demonstrating what
 * Obondo actually does — visually reinforcing "Kenya's first AI-enabled
 * trading evaluation platform" claim.
 *
 * Props:
 *   line     — the message Obondo says.
 *   variant  — visual tone: "positive" (green) · "negative" (red) · "neutral" (default blue-ish).
 *   testId   — data-testid for automated testing.
 *
 * Behaviour:
 * - Types the line character-by-character on mount (~2.5s, steps() CSS animation).
 * - Displays a subtle pulsing "typing" dot beside Obondo's name.
 * - Hidden on true mobile viewports via `.obondo-bubble` media query — the
 *   bubble is a *marketing* device that reads best on desktop/tablet mockups.
 */
export default function ObondoBubble({ line, variant = "neutral", testId }) {
    const [typed, setTyped] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setTyped(true), 2800);
        return () => clearTimeout(t);
    }, []);

    return (
        <div
            className={`obondo-bubble obondo-bubble--${variant}`}
            data-testid={testId || "obondo-bubble"}
            role="complementary"
            aria-label={`Obondo says: ${line}`}
        >
            <div className="obondo-bubble__avatar" aria-hidden="true">O</div>
            <div className="obondo-bubble__body">
                <div className="obondo-bubble__header">
                    <span className="obondo-bubble__name">Obondo</span>
                    <span
                        className={`obondo-bubble__typing ${typed ? "obondo-bubble__typing--done" : ""}`}
                        aria-hidden="true"
                    >
                        <span /><span /><span />
                    </span>
                </div>
                <div
                    className="obondo-bubble__line"
                    style={{ "--obondo-bubble-chars": line.length }}
                >
                    {line}
                </div>
            </div>
            <span className="obondo-bubble__glow" aria-hidden="true" />
        </div>
    );
}
